import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { enqueueSnackbar } from "notistack";
import { PollDataRequest, PollData } from "../../../types/stage-mode";
import { APIResponseType } from "@/types/generics/APIResponse.type";

export const StageModeAPI = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

StageModeAPI.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Talent: Create StageCard (POST)
export const useCreateStageCard = () => {
  return useMutation({
    mutationFn: (payload: any) => StageModeAPI.post("/stagecards", payload),
    onError: (error) => {
      console.error("Failed to create StageCard:", error);
    },
  });
};

// Talent: List StageCards (GET)
export const useListStageCards = () => {
  return useQuery({
    queryKey: ["stagecards"],
    queryFn: async () => {
      const res = await StageModeAPI.get("/stagecards?offset=0&limit=1000");
      return res.data;
    },
    staleTime: 15000,
    retry: 1,
  });
};

// Talent: List StageCards (GET)
export const useBookingList = (
  status: string,
  talentId?: string,
  businessId?: string
) => {
  return useQuery({
    queryKey: ["stagecards-booking", talentId, businessId, status],
    queryFn: async () => {
      const res = await StageModeAPI.get("/stagecards/bookings", {
        headers: {
          ...(talentId && { "talent-id": talentId }),
        },
        params: {
          ...(businessId && { business_id: businessId }),
          status: status,
        },
      });
      return res.data;
    },
    staleTime: 15000,
    retry: 1,
  });
};

// Talent: List StageCards (GET)
export const useGetBookingById = (bookingId: string) => {
  return useQuery({
    queryKey: ["stagecards-booking", bookingId],
    queryFn: async () => {
      const res = await StageModeAPI.get(`/stagecards/booking/${bookingId}`);
      return res.data;
    },
    staleTime: 15000,
    retry: 1,
    enabled: !!bookingId,
  });
};

// Talent: List StageCards (GET)
export const useTalentListStageCards = (
  talentId?: string,
  businessId?: string
) => {
  return useQuery({
    queryKey: ["stagecards", talentId, businessId],
    queryFn: async () => {
      const res = await StageModeAPI.get("/stagecards/me", {
        headers: {
          ...(talentId && { "talent-id": talentId }),
        },
        params: {
          ...(businessId && { business_id: businessId }),
        },
      });
      return res.data;
    },
    staleTime: 15000,
    retry: 1,
    enabled: !!talentId || !!businessId,
  });
};

// Talent: Get StageCard by ID (GET)
export const useGetStageCard = (id: string) => {
  return useQuery({
    queryKey: ["stagecard", id],
    queryFn: async () => {
      const res = await StageModeAPI.get(`/stagecards/${id}`);
      return res.data;
    },
    enabled: !!id,
    staleTime: 10000,
    retry: 1,
  });
};

// Talent: Accept or reject booking
export const useRespondToBooking = (talentId?: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      bookingId,
      status,
      business_id,
    }: {
      bookingId: string;
      status: "accept" | "reject";
      business_id?: string;
    }) =>
      StageModeAPI.post(
        `/stagecards/booking/${bookingId}/${status}`,
        {
          ...(business_id && { business_id }),
        },
        {
          headers: {
            ...(talentId && { "talent-id": talentId }),
          },
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["stagecards-booking", talentId],
      });
    },
    onError: (error) => {
      console.error("Failed to respond to booking:", error);
    },
  });
};

export const useStartEndStageSession = (talentId?: string | undefined) => {
  return useMutation({
    mutationFn: ({
      bookingId,
      status,
      business_id,
    }: {
      bookingId: string;
      status: "start" | "end";
      business_id?: string;
    }) =>
      StageModeAPI.post(
        `/stagecards/booking/${bookingId}/${status}`,
        {
          ...(business_id && { business_id }),
        },
        {
          headers: {
            ...(talentId && { "talent-id": talentId }),
          },
        }
      ),
    onError: (error: any) => {
      enqueueSnackbar(
        error?.response?.data?.message || "Failed to start stage session",
        {
          variant: "error",
        }
      );
    },
  });
};

export const useCreatePoll = () => {
  return useMutation({
    mutationFn: ({
      bookingId,
      data,
    }: {
      bookingId: string;
      data: PollDataRequest;
    }) => StageModeAPI.post(`/stagecards/booking/${bookingId}/poll`, data),
    onSuccess: () => {
      enqueueSnackbar("Poll created successfully", {
        variant: "success",
      });
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error?.response?.data?.message || "Failed to create poll",
        {
          variant: "error",
        }
      );
    },
  });
};

export const useEndPoll = () => {
  return useMutation({
    mutationFn: ({
      bookingId,
      pollId,
    }: {
      bookingId: string;
      pollId: string;
    }) =>
      StageModeAPI.post(
        `/stagecards/booking/${bookingId}/poll/${pollId}/close`,
        {}
      ),
    onSuccess: () => {
      enqueueSnackbar("Poll ended successfully", {
        variant: "success",
      });
    },
    onError: (error: any) => {
      enqueueSnackbar(error?.response?.data?.message || "Failed to end poll", {
        variant: "error",
      });
    },
  });
};

export const useGetPoll = (id: string) => {
  return useQuery({
    queryKey: ["get-poll", id],
    queryFn: async () => {
      const res = await StageModeAPI.get<APIResponseType<PollData[]>>(
        `/stagecards/booking/${id}/poll`
      );
      return res.data;
    },
    enabled: !!id,
    staleTime: 10000,
    retry: 1,
  });
};

// Talent: Generate StageSession Token
export const useCreateStageSessionToken = () => {
  return useMutation({
    mutationFn: (payload: { stageCardId: string }) =>
      StageModeAPI.post("/stagesession/token", payload),
    onError: (error) => {
      console.error("Failed to create session token:", error);
    },
  });
};
